import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { subscribeToToasts, toast as toastApi } from '../lib/toast';

const ToastContext = createContext({
  toast: toastApi,
});

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    return subscribeToToasts((payload) => {
      setToasts((current) => [...current, payload].slice(-4));
    });
  }, []);

  useEffect(() => {
    if (!toasts.length) return undefined;

    const timers = toasts.map((item) =>
      window.setTimeout(() => dismissToast(item.id), item.duration ?? 4500)
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [toasts, dismissToast]);

  const value = useMemo(() => ({ toast: toastApi }), []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

const VARIANT_STYLES = {
  success: {
    icon: 'text-brand-green',
    border: 'border-brand-green/25',
    accent: 'bg-brand-green/10',
  },
  error: {
    icon: 'text-brand-danger',
    border: 'border-brand-danger/25',
    accent: 'bg-brand-danger/10',
  },
  info: {
    icon: 'text-brand-info',
    border: 'border-brand-info/25',
    accent: 'bg-brand-info/10',
  },
};

function ToastIcon({ variant }) {
  if (variant === 'success') {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (variant === 'error') {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <circle cx="12" cy="12" r="10" />
        <path d="m15 9-6 6M9 9l6 6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
    </svg>
  );
}

function Toaster({ toasts, onDismiss }) {
  if (!toasts.length) return null;

  return (
    <div
      aria-live="polite"
      aria-relevant="additions"
      className="pointer-events-none fixed inset-x-0 bottom-20 z-[60] flex flex-col items-center gap-2 px-4 lg:bottom-6 lg:items-end"
    >
      {toasts.map((item) => {
        const styles = VARIANT_STYLES[item.variant] || VARIANT_STYLES.info;
        return (
          <div
            key={item.id}
            role="status"
            className={`pointer-events-auto flex w-full max-w-sm animate-fade-in-up items-start gap-3 rounded-xl border bg-[var(--ba-surface-bg)] p-4 shadow-lg ${styles.border}`}
          >
            <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${styles.accent} ${styles.icon}`}>
              <ToastIcon variant={item.variant} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[var(--ba-body-text)]">{item.title}</p>
              {item.description && (
                <p className="mt-0.5 text-sm ba-muted">{item.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onDismiss(item.id)}
              className="shrink-0 rounded-lg p-1 text-[var(--ba-muted)] transition-colors hover:bg-black/5 hover:text-[var(--ba-body-text)] dark:hover:bg-white/10"
              aria-label="Chiudi notifica"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}

export const useToast = () => useContext(ToastContext);
